import * as path from 'node:path';
import { getBridgeRoot } from '../files/cwd/index.js';

/**
 * Path vocabulary (BuildAutomaton CLI):
 *
 * - **Bridge root**: Directory the bridge uses for repos, skills, file index, and sessions in `bridge_root` mode.
 * - **Worktrees root**: Top-level directory containing every bridge’s isolated worktree layout (e.g. `~/.buildautomaton/worktrees`).
 * - **Bridge-key directory**: `{worktreesRoot}/{bridgeKey}` — one folder per launcher/bridge root (from the layout file).
 * - **Session directory** (`worktrees_root` parent): `{worktreesRoot}/{bridgeKey}/{sessionId}` — mirrors the bridge’s
 *   repo tree; each repo folder under it is a git worktree. This is the agent cwd and `session_parent_path`, not the
 *   global worktrees root or the bridge-key folder alone. Legacy disks may still use `{bridgeKey}/{repoRel}/{sessionId}`.
 * - **Preview environment directory**: `{worktreesRoot}/{bridgeKey}/preview/{environmentId}` — same repo mirror layout
 *   for local dev servers; updated when a session is deployed to the preview environment.
 */

function commonResolvedPathPrefix(a: string, b: string): string {
  const ar = path.resolve(a);
  const br = path.resolve(b);
  const as = ar.split(path.sep);
  const bs = br.split(path.sep);
  let i = 0;
  const min = Math.min(as.length, bs.length);
  while (i < min && as[i] === bs[i]) i += 1;
  if (i === 0) return '';
  return as.slice(0, i).join(path.sep);
}

/**
 * Worktree layout root: longest common prefix of each checkout’s parent directory (under the worktrees root).
 * Used on the CLI for working-tree `repoRelPath` math, not for `session_parent_path`.
 */
export function computeSessionWorktreeRootPathFromRepoCheckouts(worktreePaths: string[]): string {
  if (worktreePaths.length === 0) return '';
  const dirs = worktreePaths.map((p) => path.dirname(path.resolve(p)));
  let common = dirs[0]!;
  for (let i = 1; i < dirs.length; i++) {
    common = commonResolvedPathPrefix(common, dirs[i]!);
  }
  return common;
}

/**
 * Value for `session_parent_path` / agent cwd in worktrees mode: one concrete checkout root per session.
 * Multiple repos → stable first path after sorting (same ordering as snapshot / git tooling).
 */
export function resolveIsolatedSessionParentPathFromCheckouts(worktreePaths: string[]): string | null {
  const resolved = worktreePaths.map((p) => path.resolve(p)).filter(Boolean);
  if (resolved.length === 0) return null;
  resolved.sort();
  return resolved[0]!;
}

/**
 * Session parent path for the agent process: resolved path from session meta when set,
 * otherwise the bridge root.
 */
export function resolveSessionParentPathForAgentProcess(resolvedSessionParentPath: string | undefined): string {
  if (resolvedSessionParentPath != null && String(resolvedSessionParentPath).trim() !== '') {
    return path.resolve(String(resolvedSessionParentPath).trim());
  }
  return getBridgeRoot();
}
